import React, { useEffect, useState } from "react";
import { getTranslateByKey, removeEmptyValues } from "@/main/services/utils";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { toast } from "react-toastify";
import * as S from "./styles";
import KogInputText from "@/main/components/shared/inputText/inputText";
import { DamageCategoryRequest } from "@/main/models/request/damageCategoryRequest";
import { damageCategoryApi } from "@/main/api";
import useAsyncModalConfirmReason from "@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";
import { DamageCategoryResponse } from "@/main/models/response/damageCategoryResponse";

interface Props {
	open?: boolean;
	closeModal: () => void;
	guidInsurer: string | null;
	damageCategoryEdit?: DamageCategoryResponse | null;
	loadGridList: () => void;
}

function DamageCategorySidebar({ open, closeModal, guidInsurer, damageCategoryEdit, loadGridList }: Props) {
	const [isLoading, setIsLoading] = useState(false);
	const [name, setName] = useState<string>("");
	const [errorName, setErrorName] = useState<string | null>(null);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

	useEffect(() => {
		if (damageCategoryEdit) {
			setName(damageCategoryEdit?.damageCategoryName);
		} else {
			setName("");
		}
	}, [open]);

	function validateField(value: string, setError: React.Dispatch<React.SetStateAction<string | null>>, label: string) {
		if (value.length === 0 || value === "") {
			setError(label);
			return false;
		}
		setError(null);
		return true;
	}

	function validateForm() {
		const isValidName = validateField(name, setErrorName, getTranslateByKey("information.categoryNameError", true));

		return isValidName;
	}

	function getModalTitle() {
		if (damageCategoryEdit) {
			return `${getTranslateByKey("information.editCategoryName")}`;
		} else {
			return `${getTranslateByKey("information.addCategory")} `;
		}
	}

	async function onSubmitSave() {
		try {
			if (!validateForm()) return;

			let resultConfirm = null;
			let filesRequest = undefined;

			resultConfirm = await getConfirmationReason(
				getTranslateByKey("information.confirmChangeTitle"),
				ReasonTypeEnum.ChangeCompany,
			);

			if (!resultConfirm.confirmed) return;

			if (resultConfirm.files && resultConfirm.files.length > 0) {
				filesRequest = await Promise.all(
					resultConfirm.files.map(async (item) => {
						const base64DataUrl = await toBase64(item);
						return {
							fileName: item.name,
							contentType: item.type,
							fileBase64: base64DataUrl as string,
						};
					}),
				);
			}

			setIsLoading(true);

			const requestDamageCategory = removeEmptyValues({
				guidInsurer,
				damageCategoryName: name,
				guidDamageCategory: damageCategoryEdit?.guidDamageCategory,
			}) as DamageCategoryRequest;

			requestDamageCategory.changeJustification = {
				guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
				reasonDescription: resultConfirm?.observation || null,
				filesUpload: filesRequest,
			};

			await damageCategoryApi.saveDamageCategory(requestDamageCategory);

			toast.success(getTranslateByKey("information.categorySaveSuccessfully"));

			setName("");
			loadGridList();
			closeModal();
		} catch {
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<>
			{open && (
				<KogModalSideComponent open={open} closeModal={() => closeModal()} id="id-damageCategory-modal-scroll">
					<S.HeaderText>{getModalTitle()}</S.HeaderText>
					<S.Container>
						<KogInputText
							id="id-category-name-input"
							label={`${getTranslateByKey("information.name")} `}
							name="name"
							value={name}
							required
							errorMessageCustom={errorName}
							inputProps={{ maxLength: 100 }}
							onChangeCustom={(e) => {
								setName(e);
								validateField(e, setErrorName, getTranslateByKey("information.categoryNameError", true));
							}}
						/>
					</S.Container>
					<S.AreaButton>
						<SecondaryButton id="id-damageCategory-btn-save" type="submit" isLoading={isLoading} onClick={onSubmitSave}>
							{getTranslateByKey("information.save")}
						</SecondaryButton>
					</S.AreaButton>
				</KogModalSideComponent>
			)}
			<ModalConfirmationReason closeByOverlay={false} />
		</>
	);
}

export default DamageCategorySidebar;
